import { useParams } from 'react-router-dom';

import { isJudgeParty, useSession } from '@/lib/auth/session';

/**
 * 지금 보고 있는 현장.
 *
 * <p>현장 이름을 URL 이 아니라 <b>세션에서</b> 찾는다 — 서버가 준 배정 목록에 없는 현장이면
 * 주소창에 UUID 를 넣어도 이름이 나오지 않는다. 진짜 차단은 서버가 하지만(데이터 경계),
 * 화면도 모르는 현장을 아는 척하지 않는다.
 *
 * <h2>손이 셋이다 (DOC-87 §3-3)</h2>
 * 화면이 버튼을 세우는 축은 주체가 아니라 <b>손</b>이다.
 * <ul>
 *   <li>{@code isJudge} — 판정하는 손. 감리와 직원. 검측 착수·판정, NCR 발행·확인, 안전 회차·지적·확인,
 *       도면 검토, 성적서 판정, 장비 확인, 납품 검수</li>
 *   <li>{@code canSubmitEvidence} — 증빙을 만드는 손. 협력사와 직원. 검측 요청·재검, 사진 붙이기</li>
 *   <li>{@code isVendor} — 협력사만. 조치 회신·제출·등록·출역·기성·계약·TBM 서명</li>
 *   <li>{@code isStaff} — 직원만. 작업일보 작성·제출</li>
 * </ul>
 * 「판정하는 손이 증빙을 만들지 않는다」는 선은 <b>감리에게만</b> 남는다 — 직원은 front 에서 이미
 * 요청·사진·판정을 다 한다(DOC-87 §3-4). 셋 다 {@code readOnly}(끝난 배정)면 꺼진다.
 */
export function useSite() {
  const { siteId = '' } = useParams();
  const session = useSession();
  const site = session.data?.sites.find((it) => it.id === siteId);
  const partyType = session.data?.partyType ?? 'VENDOR';
  /** 배정이 끝난 현장. 조회만 되고 쓰기는 서버가 403 으로 막는다. */
  const readOnly = site?.readOnly ?? false;

  return {
    siteId,
    siteName: site?.name ?? '',
    readOnly,
    known: site !== undefined,
    partyType,
    isJudge: isJudgeParty(partyType) && !readOnly,
    canSubmitEvidence: (partyType === 'VENDOR' || partyType === 'STAFF') && !readOnly,
    isVendor: partyType === 'VENDOR' && !readOnly,
    isSupervisor: partyType === 'SUPERVISOR' && !readOnly,
    isStaff: partyType === 'STAFF' && !readOnly,
  };
}
